import { Link } from "react-router-dom";
import {
  BadgeCheck,
  Headphones,
  PackageSearch,
  RotateCcw,
  ShieldCheck,
  Truck,
  Wallet,
} from "lucide-react";
import { useSettings } from "context/SettingsContext";
import { FaCheckCircle } from "react-icons/fa";

const highlightCards = [
  {
    title: "Fast Delivery",
    description: "We deliver your order quickly and safely to your doorstep",
    icon: Truck,
    accent: "from-primary-500 to-cyan-400",
  },
  {
    title: "Support",
    description:
      "Helpful customer support when you need guidance before or after ordering.",
    icon: Headphones,
    accent: "from-emerald-500 to-teal-400",
  },
  {
    title: "Secure Shopping",
    description:
      "A safer buying flow with trusted product handling and reliable checkout.",
    icon: ShieldCheck,
    accent: "from-violet-500 to-fuchsia-400",
  },
  {
    title: "Lots of Product",
    description:
      "A wide collection of products so customers can explore more in one place.",
    icon: PackageSearch,
    accent: "from-amber-500 to-orange-400",
  },
];

const missionPoints = [
  "Offer genuine products at affordable prices.",
  "Deliver products fast and with care.",
  "Build trust through quality products and honest service.",
];

const chooseCards = [
  {
    title: "Authentic Products",
    text: "Every product is carefully sourced to ensure authenticity, quality, and customer satisfaction.",
    icon: BadgeCheck,
  },
  {
    title: "Easy Return & Refund",
    text: "If you receive a damaged or incorrect item, we offer a simple return and refund process.",
    icon: RotateCcw,
  },
  {
    title: "Cash on Delivery",
    text: "Shop with confidence and pay only after your order is delivered to your doorstep.",
    icon: Truck,
  },
  {
    title: "Best Value Pricing",
    text: "We offer competitive prices with the goal of providing premium products at affordable rates.",
    icon: Wallet,
  },
];

const AboutUs = () => {
  const { theme } = useSettings();
  const isDark = theme === "dark";

  return (
    <div
      className={`relative overflow-hidden select-none ${
        isDark ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-900"
      }`}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 right-0 h-72 w-72 rounded-full bg-primary-500/15 blur-3xl" />
        <div className="absolute top-44 -left-20 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-4 sm:px-5 md:px-6 md:py-14">
        <section className="overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white shadow-2xl">
          <div className="px-5 py-5 sm:px-6 md:px-10 md:py-14">
            <div>
              <h1 className=" text-xl text-center font-bold leading-tight sm:text-3xl md:text-5xl">
                Welcome to Nabil Fashion
              </h1>
              <p className="md:mt-4 mt-3 text-center text-xs md:leading-7 leading-6 text-slate-200 sm:mt-5 md:text-base">
                We make online shopping feel simple and trustable. From the
                first visit to the final delivery, we try to keep the experience
                clear, fast, and customer-friendly.
              </p>

              <div className="md:mt-6 mt-3 flex gap-3 ">
                <Link
                  to="/"
                  className="w-full rounded-full bg-white px-5 md:py-3 py-2 text-center md:text-sm text-xs font-semibold text-slate-900 transition  sm:w-auto"
                >
                  Browse Products
                </Link>
                <Link
                  to="/contactUs"
                  className="w-full rounded-full border border-white/20 bg-white/10 px-5 md:py-3 py-2 text-center md:text-sm text-xs font-semibold text-white transition sm:w-auto"
                >
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="md:mt-10 mt-6">
          <div className="mt-6 grid grid-cols-2 xl:grid-cols-4 md:gap-4 gap-2 items-stretch">
            {highlightCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className={`group h-full rounded-2xl border ${
                    isDark
                      ? "border-white/10 bg-slate-900/80"
                      : "border-slate-200 bg-white"
                  } md:p-5 p-3 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl`}
                >
                  <div className="flex h-full flex-col items-center text-center mt-1">
                    <div
                      className={`flex md:h-12 h-8 md:w-12 w-8 items-center justify-center rounded-2xl bg-gradient-to-br ${card.accent} text-white shadow-lg`}
                    >
                      <Icon className="md:h-6 h-4 md:w-6 w-4 " />
                    </div>

                    <h3 className="md:mt-3 mt-2 md:text-lg font-semibold">
                      {card.title}
                    </h3>

                    <p className="mt-2 md:text-sm text-xs md:leading-6 leading-5 text-slate-500">
                      {card.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-10 grid gap-6 lg:grid-cols-[1fr_0.95fr]">
          <div
            className={`rounded-3xl border p-6 md:p-8 ${
              isDark
                ? "border-white/10 bg-slate-900/80"
                : "border-slate-200 bg-white"
            } shadow-sm`}
          >
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-500">
              Our Mission
            </p>
            {/*<h2 className="md:mt-3 mt-2 md:font-bold font-medium md:text-3xl text-lg md:leading-7 leading-5">*/}
            {/*  Making shopping easier for everyone*/}
            {/*</h2>*/}
            <p className="md:mt-4 mt-2 text-xs md:leading-7 leading-6 text-slate-500 md:text-base">
              Our mission is to make the buying journey straightforward and
              trustworthy, so customers can shop with confidence and receive
              what they need without unnecessary hassle.
            </p>

            <div className="md:mt-6 mt-3 space-y-3">
              {missionPoints.map((point) => (
                <div
                  key={point}
                  className={`flex items-center gap-3 rounded-2xl md:p-4 p-3 ${
                    isDark ? "bg-white/5" : "bg-slate-50"
                  }`}
                >
                  <div className="mt-0.5 rounded-full bg-emerald-500/15 p-2 text-emerald-500">
                    <BadgeCheck className="h-4 w-4" />
                  </div>
                  <p className="text-sm md:leading-6 leading-5 text-slate-600">
                    {point}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div
            className={`rounded-3xl border mb-16 p-6 md:p-8 ${
              isDark
                ? "border-white/10 bg-gradient-to-br from-slate-900 to-slate-800"
                : "border-slate-200 bg-gradient-to-br from-white to-slate-50"
            } shadow-sm`}
          >
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-primary-600">
              Why Shop With Us
            </p>
            {/*<h2 className="md:mt-3 mt-2 md:font-bold font-medium md:text-3xl text-lg leading-5 md:leading-7">*/}
            {/*  A better shopping experience, built around trust*/}
            {/*</h2>*/}
            <p className="md:mt-4 mt-2 text-xs md:leading-7 leading-5 text-slate-500 md:text-base">
              We design the shopping journey to feel calm, clear, and reliable
              from start to finish.
            </p>
            <div className="md:mt-6 mt-5 grid grid-cols-1  gap-4">
              {chooseCards.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 text-primary-500 flex-shrink-0" />

                  <div>
                    <h3 className="font-semibold text-sm md:text-base">
                      {item.title}
                    </h3>

                    <p
                      className={`mt-1 text-xs md:text-sm leading-5 ${
                        isDark ? "text-slate-300" : "text-slate-600"
                      }`}
                    >
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutUs;
